import { useRouter } from "next/router";
import { useMemo } from "react";

import { RouteConfig } from "@/config/route.config";
import { AuthContext } from "@/data/auth/auth.context";
import { CommonModels } from "@/data/common/common.models";

/**
 * Hook that combines authentication state with onboarding validation
 * to determine if the user should see the sidebar layout
 */
export const useAuthAndOnboardingCheck = () => {
  const { isInitializing, isLoggedIn, user } = AuthContext.useAuth();
  const router = useRouter();

  const isSettingsRoute = useMemo(() => {
    return router.pathname === RouteConfig.Settings.slice(0, -1);
  }, [router.pathname]);

  const isProfileEditRoute = useMemo(() => {
    return router.pathname.replace("/[section]", "") === RouteConfig.Edit.Index.slice(0, -1).replace("[section]", "");
  }, [router.pathname]);

  const isOnboardingRoute = useMemo(() => {
    return router.pathname === RouteConfig.Onboarding.slice(0, -1);
  }, [router.pathname]);

  const isProtectedRoute = useMemo(() => {
    return [
      RouteConfig.Profile.slice(0, -1),
      RouteConfig.VipPortfolios.slice(0, -1),
      RouteConfig.MyWallet.slice(0, -1),
      RouteConfig.Analytics.slice(0, -1),
      RouteConfig.Watchlist.slice(0, -1),
      RouteConfig.Liveshow.slice(0, -1),
    ].includes(router.pathname);
  }, [router.pathname]);

  const isUserOnboarded = useMemo(() => {
    if (!user) return false;
    return user.onboardingStep === CommonModels.OnboardingStepEnum.completed;
  }, [user]);

  const shouldShowSidebarLayout = useMemo(() => {
    // Don't show sidebar if still initializing
    if (isInitializing) return false;

    if (isSettingsRoute) return false;

    if (isProfileEditRoute) return false;

    // Don't show sidebar on onboarding routes
    if (isOnboardingRoute) return false;

    if (router.pathname === RouteConfig.Success.slice(0, -1)) return false;

    if (router.pathname === RouteConfig.Signup.slice(0, -1)) return false;

    if (router.pathname === RouteConfig.Signin.slice(0, -1)) return false;

    // Don't show sidebar on bug-report route (should use regular layout)
    if (router.pathname === RouteConfig.BugReport.slice(0, -1)) return false;

    // For protected routes, only show sidebar if user is logged in AND onboarded
    if (isProtectedRoute) {
      return isLoggedIn && isUserOnboarded;
    }

    // For other routes, show sidebar if user is logged in
    return isLoggedIn;
  }, [
    isInitializing,
    isOnboardingRoute,
    isProtectedRoute,
    isLoggedIn,
    isUserOnboarded,
    router.pathname,
    isSettingsRoute,
    isProfileEditRoute,
  ]);

  const isCheckingAuth = useMemo(() => {
    return isInitializing || (isProtectedRoute && isLoggedIn && !user);
  }, [isInitializing, isProtectedRoute, isLoggedIn, user]);

  return {
    shouldShowSidebarLayout,
    isCheckingAuth,
    isOnboardingRoute,
    isProtectedRoute,
    isUserOnboarded,
  };
};
